import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  BxBadgeComponent,
  BxBreadcrumb,
  BxButtonComponent,
  BxTabComponent,
  BxTabGroupComponent,
  BxTooltipDirective,
  injectBxIcons,
  checkIcon,
  tool02Icon,
} from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { EnvironmentService } from '../../../environments/environment.service';
import { StateBadgeComponent } from '../../components/state-badge/state-badge.component';
import { StateBadge } from '../../components/state-badge/state-badge.models';
import { StatusBadgeComponent } from '../../components/status-badge/status-badge.component';
import { Cardinality, isCardinality } from '../../models/device.models';
import { isEligibleForMaintenanceOrder, isMaintenanceOrderOngoing } from '../../models/maintenance';
import { CommonTranslateService } from '../../services/common-translate/common-translate.service';
import { MaintenanceOrderCreationService } from '../../services/maintenance-order-creation/maintenance-order-creation.service';
import { PermissionService } from '../../services/permission/permission.service';
import { BROWSER_TAB_SUFFIX } from '../../utils/utils';
import { PageComponent } from '../page/page.component';
import { BuildingHeatingDisruptionComponent } from './building-heating-disruption/building-heating-disruption.component';
import { DeviceAttributesComponent } from './device-attributes/device-attributes.component';
import { DeviceContextComponent } from './device-context/device-context.component';
import { DETAILS_PAGE_TABS } from './device-details.models';
import { DeviceDetailsStore } from './device-details.store';
import { DeviceRawDataComponent } from './device-raw-data/device-raw-data.component';
import { DeviceReceptionHistoryComponent } from './device-reception-history/device-reception-history.component';
import { DeviceSettingsPanelComponent } from './device-settings-panel/device-settings-panel.component';
import { LorawanSettingsEssentialComponent } from './device-settings/lorawan-gateway/lorawan-settings-essential/lorawan-settings-essential.component';
import { SrtSettingsEssentialComponent } from './device-settings/srt/srt-settings-essential/srt-settings-essential.component';
import { SrtSettingsResidentConfigurationComponent } from './device-settings/srt/srt-settings-resident-configuration/srt-settings-resident-configuration.component';
import { SrtSettingsResidentOperationComponent } from './device-settings/srt/srt-settings-resident-operation/srt-settings-resident-operation.component';
import { SrtSettingsSystemComponent } from './device-settings/srt/srt-settings-system/srt-settings-system.component';
import { TsSettingsEssentialComponent } from './device-settings/temperature-sensor/ts-settings-essential/ts-settings-essential.component';
import { DeviceStatusComponent } from './device-status/device-status.component';
import { GrafanaChartCardComponent } from './grafana-chart-card/grafana-chart-card.component';
import type { GrafanaChartTab } from './grafana-chart-card/grafana-chart-card.models';
import { MaintenanceWidgetComponent } from './maintenance-widget/maintenance-widget.component';
import { BasicMbusGatewayCurrentStateComponent } from './basic-mbus-gateway-current-state/basic-mbus-gateway-current-state.component';
import { LorawanCurrentStateComponent } from './lorawan-current-state/lorawan-current-state.component';
import { BasicMbusGatewayReceivedDevicesComponent } from './basic-mbus-gateway-received-devices/basic-mbus-gateway-received-devices.component';
import { LorawanReceivedDevicesComponent } from './lorawan-received-devices/lorawan-received-devices.component';
import { PulseGasMeterCurrentStateComponent } from './pulse-gas-meter-current-state/pulse-gas-meter-current-state.component';
import { QtempCurrentStateComponent } from './qtemp-current-state/qtemp-current-state.component';
import { SrtCurrentStateComponent } from './srt-current-state/srt-current-state.component';
import { SubmeteringGatewayCurrentStateComponent } from './submetering-gateway-current-state/submetering-gateway-current-state.component';
import { SubmeteringGatewayReceivedDevicesComponent } from './submetering-gateway-received-devices/submetering-gateway-received-devices.component';
import { TsCurrentStateComponent } from './ts-current-state/ts-current-state.component';

@Component({
  selector: 'bxi-device-details',
  imports: [
    PageComponent,
    BxButtonComponent,
    TranslatePipe,
    StatusBadgeComponent,
    DeviceAttributesComponent,
    BxTabComponent,
    BxTabGroupComponent,
    StateBadgeComponent,
    DeviceRawDataComponent,
    DeviceReceptionHistoryComponent,
    DeviceSettingsPanelComponent,
    SrtSettingsResidentConfigurationComponent,
    SrtSettingsResidentOperationComponent,
    SrtSettingsEssentialComponent,
    LorawanSettingsEssentialComponent,
    TsSettingsEssentialComponent,
    MaintenanceWidgetComponent,
    DeviceStatusComponent,
    BuildingHeatingDisruptionComponent,
    BxTooltipDirective,
    SrtSettingsSystemComponent,
    BxBadgeComponent,
    SrtCurrentStateComponent,
    TsCurrentStateComponent,
    QtempCurrentStateComponent,
    PulseGasMeterCurrentStateComponent,
    BasicMbusGatewayCurrentStateComponent,
    SubmeteringGatewayCurrentStateComponent,
    LorawanCurrentStateComponent,
    LorawanReceivedDevicesComponent,
    BasicMbusGatewayReceivedDevicesComponent,
    SubmeteringGatewayReceivedDevicesComponent,
    DeviceContextComponent,
    GrafanaChartCardComponent,
  ],
  providers: [DeviceDetailsStore],
  templateUrl: './device-details.component.html',
  host: { class: 'flex h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceDetailsComponent {
  private readonly domsanitizer = inject(DomSanitizer);
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly commonTranslate = inject(CommonTranslateService);
  protected readonly store = inject(DeviceDetailsStore);
  private readonly maintenanceOrderCreation = inject(MaintenanceOrderCreationService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly permissionService = inject(PermissionService);

  protected readonly activeTabId = toSignal(this.store.activeTabId$);
  protected readonly device = toSignal(this.store.device$);
  protected readonly thingId = toSignal(this.store.thingId$);
  protected readonly isSrt = toSignal(this.store.isSrt$, { initialValue: false });
  protected readonly isSubmetering = toSignal(this.store.isSubmetering$, { initialValue: false });
  protected readonly srtDevice = toSignal(this.store.srtDevice$);
  protected readonly muting = computed(() => {
    const device = this.device();
    return device?.isMuted() ? device?.features?.monitoring?.properties?.configuration?.muting : undefined;
  });
  protected readonly tabIds = DETAILS_PAGE_TABS;

  protected readonly hasMaintenance = computed(() => !!this.device()?.features?.maintenance?.properties);
  protected readonly maintenanceStatusName = computed(() => this.device()?.features?.maintenance?.properties?.status?.name as StateBadge);
  protected readonly maintenanceLabel = computed(() => {
    return `${this.translate.instant('system.maintenance')}: ${this.commonTranslate.translateState(this.maintenanceStatusName())}`;
  });
  protected readonly hasOngoingMaintenance = computed(() => isMaintenanceOrderOngoing(this.maintenanceStatusName()));
  protected readonly canCreateMaintenance = toSignal(this.permissionService.canCreateMaintenance$, { initialValue: false });
  protected readonly createOrderInProgress = signal(false);
  protected readonly createMaintenanceOrderBlocked = computed(() => {
    const device = this.device();
    return !this.canCreateMaintenance() || this.createOrderInProgress() || !device || !isEligibleForMaintenanceOrder(device);
  });

  // Only Dragino temperature sensors expose downlink settings; Qundis sensors get the chart only.
  protected hasSettingsTab = computed(() => this.device()?.isSrt() || this.device()?.isKerlinkGateway() || this.isDraginoSensor());
  protected hasDeviceStatusTab = computed(() => !!this.device()?.features?.deviceStatus?.properties?.status);

  protected readonly breadcrumbOptions = computed<BxBreadcrumb[]>(() => [
    { label: this.translate.instant('navigation.devices'), route: '/devices' },
    { label: this.titleDeviceType(), route: '' },
  ]);

  private readonly grafanaBaseUrl = 'https://grafana.shared.beyonnex.io/d';
  private readonly grafanaDashboardSlug = 'bxinsight-grafana-integration';

  private readonly timeSeriesChartUrl = computed(() =>
    this.domsanitizer.bypassSecurityTrustResourceUrl(
      `${this.grafanaBaseUrl}/${this.environmentService.grafanaDashboardUid}/${this.grafanaDashboardSlug}` +
        `?orgId=${this.environmentService.grafanaOrgId}&from=now-7d&to=now&timezone=browser` +
        `&var-srt=${this.thingId()}&viewPanel=panel-1&kiosk`,
    ),
  );

  private readonly batteryChartUrl = computed(() =>
    this.domsanitizer.bypassSecurityTrustResourceUrl(
      `${this.grafanaBaseUrl}/${this.environmentService.grafanaDashboardUid}/${this.grafanaDashboardSlug}` +
        `?orgId=${this.environmentService.grafanaOrgId}&from=now-7d&to=now&timezone=browser` +
        `&var-srt=${this.thingId()}&viewPanel=panel-2&kiosk`,
    ),
  );

  // Temperature sensors (Dragino + Qundis) share a namespace but use different Grafana dashboards,
  // selected by the device's namespace. Both panels are scoped via &var-thingId.
  private readonly draginoDashboardSlug = 'dragino-timeseries';
  private readonly qundisDashboardSlug = 'temperature-sensor-timeseries';
  private readonly pulseGasMeterDashboardSlug = '6b2135d';

  protected readonly isDraginoSensor = computed(() => !!this.device()?.isDraginoTemperatureSensor());
  protected readonly isQundisSensor = computed(() => !!this.device()?.isQundisTemperatureSensor());
  protected readonly isPulseGasMeter = computed(() => !!this.device()?.isPulseGasMeter());
  protected readonly isLorawanGateway = computed(() => !!this.device()?.isLorawanGateway());
  protected readonly isBasicMbusGateway = computed(() => !!this.device()?.isBasicMbusGateway());
  protected readonly isSubmeteringGateway = computed(() => !!this.device()?.isSubmeteringGateway());

  private readonly tsTemperatureChartUrl = computed(() =>
    this.isDraginoSensor()
      ? this.grafanaChartUrl(this.environmentService.draginoGrafanaDashboardUid, this.draginoDashboardSlug, 'panel-2')
      : this.grafanaChartUrl(this.environmentService.qundisGrafanaDashboardUid, this.qundisDashboardSlug, 'panel-2'),
  );

  private readonly tsBatteryChartUrl = computed(() =>
    this.grafanaChartUrl(this.environmentService.draginoGrafanaDashboardUid, this.draginoDashboardSlug, 'panel-3'),
  );

  private readonly gasVolumeChartUrl = computed(() =>
    this.grafanaChartUrl(this.environmentService.pulseGasMeterGrafanaDashboardUid, this.pulseGasMeterDashboardSlug, 'panel-1'),
  );

  protected readonly srtChartTabs = computed<GrafanaChartTab[]>(() => [
    { title: 'Temperature', url: this.timeSeriesChartUrl() },
    { title: 'Battery', url: this.batteryChartUrl() },
  ]);

  protected readonly tsChartTabs = computed<GrafanaChartTab[]>(() => {
    const tabs = [{ title: 'Temperature', url: this.tsTemperatureChartUrl() }];
    return this.isDraginoSensor() ? [...tabs, { title: 'Battery', url: this.tsBatteryChartUrl() }] : tabs;
  });

  protected readonly gasMeterChartTabs = computed<GrafanaChartTab[]>(() => [{ title: 'Meter reading', url: this.gasVolumeChartUrl() }]);

  private grafanaChartUrl(dashboardUid: string, dashboardSlug: string, viewPanel: string): SafeResourceUrl {
    return this.domsanitizer.bypassSecurityTrustResourceUrl(
      `${this.grafanaBaseUrl}/${dashboardUid}/${dashboardSlug}` +
        `?orgId=${this.environmentService.grafanaOrgId}&from=now-7d&to=now&timezone=browser` +
        `&var-thingId=${this.thingId()}&viewPanel=${viewPanel}&kiosk`,
    );
  }

  protected readonly statusCardinality = computed<Cardinality | undefined>(() => {
    const cardinality = this.device()?.features?.deviceStatus?.properties?.status?.effectiveSeverity?.level?.cardinality;

    if (!isCardinality(cardinality)) {
      return;
    }

    return cardinality;
  });

  protected readonly titleDeviceType = computed(
    () => `${this.translate.instant(`thing.type.abbreviation.${this.device()?.attributes?.type}`)} ${this.device()?.attributes?.serial}`,
  );

  constructor() {
    injectBxIcons([checkIcon, tool02Icon]);
    effect(() => {
      this.titleService.setTitle(`${this.titleDeviceType()}${BROWSER_TAB_SUFFIX}`);
    });
    effect(() => {
      // prevent setting tab before device is loaded
      if (!this.device()) {
        return;
      }
      const activeTabId = this.hasDeviceStatusTab() ? DETAILS_PAGE_TABS.DEVICE_STATUS : DETAILS_PAGE_TABS.DEVICE_DATA;
      this.store.setActiveTabId(activeTabId);
    });
    // if query params change, fetch new device data
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((qp) => {
      const thingId = qp.get('thingId');
      if (!thingId) {
        return;
      }
      this.store.initialize(thingId);
    });
  }

  openCreateServiceOrderModal(): void {
    const device = this.device();
    if (!device) {
      console.error('Device is not available');
      return;
    }

    this.createOrderInProgress.set(true);
    this.maintenanceOrderCreation
      .createForDevice(device)
      .pipe(finalize(() => this.createOrderInProgress.set(false)))
      .subscribe();
  }
}
